import React, { useRef, useState, useMemo, useEffect } from "react";
import "../../App.css";
import { useLanguage } from "../../context/LanguageContext";
import { usePhotos } from "../../context/PhotoContext";
import { CollageLayoutsData } from "./CollageLayouts";

const Sidebar = () => {
  const { t } = useLanguage();
  const { photos, addPhotos, removePhoto, usedPhotoIds, addUsedPhoto, setSelectedPhoto } = usePhotos();
  const fileInputRef = useRef();
  const [activeTab, setActiveTab] = useState("photo"); // "photo" | "layout"
  const [sortOrder, setSortOrder] = useState("upload"); // "upload" | "a-z" | "z-a"
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hideUsed, setHideUsed] = useState(false);
  const handleAddPhotos = () => fileInputRef.current.click();
  const { setSelectedLayout } = usePhotos();
  const [activeLayoutId, setActiveLayoutId] = useState(null);
  const queryParams = new URLSearchParams(window.location.search);
  const productId = queryParams.get("product_id");
  const [activePageType, setActivePageType] = useState(null);

  // 🟢 NEW: Listen for layout updates from the Product component
  useEffect(() => {
    window.updateSidebarLayout = (layoutId) => {
      setActiveLayoutId(layoutId);
    };

    // Cleanup
    return () => {
      delete window.updateSidebarLayout;
    };
  }, []);
  const handleDragStart = (e, src) => {
    // Pass the source so the canvas knows which image to load
    e.dataTransfer.setData("imageSrc", src);
  };
  const hiddenLayoutProducts = [
    "1937", "1939", "1942", "1944", "1947", "1949",
    "1951", "1953", "1955", "1957", "1959", "1961",
    "1963", "1966", "1968", "1970", "1972", "1975",
    "1977", "1982", "1983", "1985", "1822", "1827",
    "1829", "1831", "1833", "1835", "1838", "1840",
    "1842", "1843", "1844", "1845", "1846", "1847",
    "1848", "1849", "1850", "1850", "1851", "1852",
    "1853", "1854", "1855", "1856", "1986", "1988",
    "1990", "1992", "2889", "2083", "2085", "2087",
    "2089", "2091", "2093", "2095", "2097", "2099",
    "2101", "2102", "2103", "2104", "2106", "2108",
    "2110", "2112", "2114", "2116", "2118", "2120",
    "2121", "2123", "2125", "2127", "2129", "2131",
    "2133", "2135", "2140", "2143", "2144", "2146",
    "2148", "2151", "2153", "2155", "2157", "2159",
    "2161", "2163", "2165", "2167", "2169", "2171",
    "2173", "2177", "2179", "2182", "2184", "2186",
    "2188", "2190", "2192", "2194", "2043", "2046",
    "2059", "2060", "2061", "2031", "2034", "1836",
  ];

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files.length) return;

    addPhotos(files);

    if (files.length === 1 && photos.length === 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedPhoto({ id: Date.now(), src: reader.result, name: file.name });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectLayout = (layout) => {
    setSelectedLayout(layout);
    setActiveLayoutId(layout.id);

    if (window.handleLayoutChange) {
      window.handleLayoutChange(layout);
    }
  };

  const filteredPhotos = useMemo(() => {
    if (!photos) return [];

    let sorted = [...photos];

    if (sortOrder === "a-z") sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortOrder === "z-a") sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));

    if (searchQuery.trim() !== "")
      sorted = sorted.filter((photo) =>
        (photo.name || "").toLowerCase().includes(searchQuery.toLowerCase())
      );

    if (hideUsed) sorted = sorted.filter((photo) => !usedPhotoIds.includes(photo.id));

    return sorted;
  }, [photos, sortOrder, searchQuery, hideUsed, usedPhotoIds]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".sort-dropdown-container")) setSortDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sortOptions = [
    { value: "upload", label: "Upload Order" },
    { value: "a-z", label: "A-Z" },
    { value: "z-a", label: "Z-A" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      const currentPageType = window.currentActivePageType || null;
      if (currentPageType !== activePageType) {
        setActivePageType(currentPageType);
      }
    }, 200);

    return () => clearInterval(interval);
  }, [activePageType]);

  const SPECIAL_PRODUCTS = ["1903", "1994", "1997", "1999", "2001"];
  const isLayoutTabHidden = useMemo(() => {
    if (SPECIAL_PRODUCTS.includes(productId)) {
      return activePageType !== "image";
    }
    if (hiddenLayoutProducts.includes(productId)) return true;
    return false;
  }, [productId, activePageType, hiddenLayoutProducts]);

  return (
    <aside className="sidebar" style={{ position: 'sticky', }}>
      {/* 🔹 Tabs */}
      <div className="sidebar-tabs">
        <button
          className={`tab ${activeTab === "photo" ? "active" : ""}`}
          onClick={() => setActiveTab("photo")}
        >
          {t("photoTab") || "PHOTO"}
        </button>

        {!isLayoutTabHidden && (
          <button
            className={`tab ${activeTab === "layout" ? "active" : ""}`}
            onClick={() => setActiveTab("layout")}
          >
            {t("layoutTab") || "LAYOUT"}
          </button>
        )}
      </div>

      {/* 📸 PHOTO TAB */}
      {activeTab === "photo" && (
        <>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          <div className="sidebar-actions">
            <button className="action-btn add-photo" onClick={handleAddPhotos}>
              {t("addPhotos") || "ADD PHOTOS"}
            </button>
          </div>

          {/* 🔹 Filters */}
          <div className="sidebar-filter">
            <div className="filter-row sort-dropdown-container">
              <i className="fa-solid fa-sort" onClick={() => setSortDropdownOpen((prev) => !prev)} style={{ cursor: "pointer" }}></i>
              <span style={{ marginLeft: "6px", cursor: "pointer" }} onClick={() => setSortDropdownOpen((prev) => !prev)}>
                {t("sortByOrder") || "Sort by order added to project"}
              </span>

              {sortDropdownOpen && (
                <div className="sort-dropdown">
                  {sortOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className="sort-option"
                      onClick={() => {
                        setSortOrder(opt.value);
                        setSortDropdownOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-row" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div>
                <input
                  type="checkbox"
                  id="hide-used"
                  checked={hideUsed}
                  onChange={(e) => setHideUsed(e.target.checked)}
                />
                <label htmlFor="hide-used" style={{ marginLeft: "5px" }}>{t("hideUsed") || "Hide Used"}</label>
              </div>

              {/* 🟢 Clear button — visible only when a canvas image exists */}
              {window.clearCanvasImage && (
                <button
                  className="clear-image-btn"
                  onClick={() => window.clearCanvasImage()}
                  style={{
                    background: "#007aff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "4px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  Clear Image
                </button>
              )}
            </div>


            <div className="search-bar">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input type="text" placeholder={t("search") || "Search"} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              <div className="border-box"></div>
            </div>
          </div>

          {/* 🔹 Photo List */}
          <div className="photo-preview-list">
            {filteredPhotos.length === 0 ? (
              <p style={{ textAlign: "center", color: "#999" }}>No photos found</p>
            ) : (
              filteredPhotos.map((photo) => (
                <div key={photo.id} className="photo-thumb-wrapper">
                  {/* Clicking photo marks it as used */}
                  <img
                    src={photo.src}
                    alt={photo.name}
                    className="photo-thumb"
                    onClick={() => {
                      setSelectedPhoto(photo);                     // Keep your UI logic
                      if (window.placeImageInSelectedCell) {
                        window.placeImageInSelectedCell(photo.src, photo.id, window.currentSelectedPageType);
                      } // Fill selected cell
                    }}
                    draggable="true"
                    onDragStart={(e) => {
                      e.dataTransfer.setData("imageSrc", photo.src);
                      e.dataTransfer.setData("imageId", photo.id);
                      e.dataTransfer.setData("imageSrc", photo.src);
                      e.dataTransfer.setData("text/plain", photo.src);
                    }}
                  />
                  <button className="remove-photo" onClick={() => removePhoto(photo.id)}>✕</button>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* 🧩 LAYOUT TAB */}
      {activeTab === "layout" && !isLayoutTabHidden && ( // Only render layout options if the tab is not hidden
        <div className="layout-options">
          {/* Sticky Header Wrapper: Set full width using negative margins and center align content */}
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              background: '#fff',
              padding: '10px 0',
              borderBottom: '1px solid #eee',
              textAlign: 'center',
              marginLeft: '-10px',
              marginRight: '-10px',
              paddingLeft: '10px',
              paddingRight: '10px'
            }}
          >
            <h3 className="layout-title" style={{ marginTop: 0, marginBottom: '10px' }}>{"Choose Layout"}</h3>
            {activeLayoutId !== null && (
              <button
                className="clear-layout-btn"
                onClick={() => {
                  setSelectedLayout(null);
                  setActiveLayoutId(null);
                  if (window.handleLayoutChange) {
                    window.handleLayoutChange(null);
                  }
                }}
                style={{
                  background: "#007AFF",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  cursor: "pointer",
                }}
              >
                Clear Layout
              </button>
            )}
          </div>
          <div className="layout-grid" style={{ paddingTop: '10px' }}>
            {CollageLayoutsData.map((layout) => (
              <div key={layout.id} className={`layout-item ${activeLayoutId === layout.id ? "active" : ""}`} onClick={() => handleSelectLayout(layout)}>
                <div className="layout-preview" style={{ position: "relative", width: "100px", height: "100px", background: "#f8f9fa", border: "1px solid #ccc", borderRadius: "6px", margin: "0 auto 8px" }}>
                  {layout.cells.map((cell, i) => (
                    <div key={i} style={{ position: "absolute", left: `${cell.x * 100}%`, top: `${cell.y * 100}%`, width: `${cell.width * 100}%`, height: `${cell.height * 100}%`, border: "1px solid #999", boxSizing: "border-box", background: "#fff" }}></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;